import React, { useEffect, useState } from 'react';
import { ArrowUpRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { apiService } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { getText } from '../../utils/language';
import { useI18n } from '../../i18n';
import LoginModal from '../auth/LoginModal.js';
import SEO from '../common/SEO';
import type { PrintifyProduct } from '../../types/printify';

const PrintifyStore: React.FC = () => {
  const { user } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();
  const i18nContext: any = useI18n();
  const { language, t } = i18nContext;
  const [artworks, setArtworks] = useState<any[]>([]);
  const [artworksLoading, setArtworksLoading] = useState(true);
  const [products, setProducts] = useState<PrintifyProduct[]>([]);
  const [printifyLoading, setPrintifyLoading] = useState(true);
  const [printifyError, setPrintifyError] = useState<string | null>(null);
  const [defaultShopId, setDefaultShopId] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);

  const formatArtworkCurrency = (amount = 0, currency?: string) => {
    const resolvedCurrency = settings?.payment?.currency || currency || 'USD';
    try {
      return new Intl.NumberFormat(language === 'pt' ? 'pt-PT' : language === 'ar' ? 'ar' : 'en', { style: 'currency', currency: resolvedCurrency }).format(amount);
    } catch {
      return `${resolvedCurrency} ${Number(amount).toFixed(2)}`;
    }
  };

  useEffect(() => {
    let active = true;
    apiService.get('/artworks', { limit: 100, surface: 'store' })
      .then((response: any) => { if (active) setArtworks(Array.isArray(response?.artworks) ? response.artworks : []); })
      .catch((error: any) => { console.error('Error loading original artworks:', error); if (active) setArtworks([]); })
      .finally(() => { if (active) setArtworksLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (settingsLoading) return;
    let active = true;
    const loadPrintify = async () => {
      const shopId = settings?.store?.defaultShopId;
      if (!shopId) {
        if (active) {
          setPrintifyError('Printify store is not configured.');
          setPrintifyLoading(false);
        }
        return;
      }
      setDefaultShopId(shopId);
      try {
        setPrintifyLoading(true);
        setPrintifyError(null);
        const response: any = await apiService.get(`/printify/shops/${shopId}/products`);
        const productList = Array.isArray(response?.data) ? response.data : [];
        if (active) setProducts(productList.filter((product: PrintifyProduct) => product.visible === true));
      } catch (error: any) {
        console.error('Error loading Printify products:', error);
        if (active) setPrintifyError(error?.response?.data?.message || 'Printed editions are temporarily unavailable.');
      } finally {
        if (active) setPrintifyLoading(false);
      }
    };
    loadPrintify();
    return () => { active = false; };
  }, [settings, settingsLoading]);

  const artworkCards = artworks.map((artwork, index) => (
    <Link key={artwork._id} to={`/store/artwork/${artwork._id}`} className="group block min-w-0">
      <div className={`overflow-hidden bg-surface ${index % 3 === 1 ? 'h-80' : 'h-[22rem]'}`}>
        <img src={artwork.images?.[0]} alt={getText(artwork.title, language)} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.025]" loading={index > 3 ? 'lazy' : 'eager'} />
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-4 border-t border-border py-4">
        <div className="min-w-0">
          <span className="editorial-eyebrow block text-[10px]">{t('store.artwork')}</span>
          <h3 className="mt-1 font-display text-2xl font-semibold leading-none">{getText(artwork.title, language)}</h3>
          {artwork.title?.ar && language !== 'ar' && <p className="mt-1 font-arabic text-lg text-text-secondary" dir="rtl" lang="ar">{artwork.title.ar}</p>}
        </div>
        <div className="flex items-start gap-2 pt-4 text-sm font-semibold text-primary">
          {formatArtworkCurrency(artwork.price, artwork.currency)} <ArrowUpRight size={17} />
        </div>
      </div>
    </Link>
  ));

  return (
    <div className="tafsir-page">
      <SEO title="Tafsir Art Store" description={t('store.subtitle')} />
      <div className="page-shell">
        <header className="max-w-5xl border-b border-border pb-9">
          <p className="editorial-eyebrow mb-3">{t('store.eyebrow')}</p>
          <h1 className="editorial-title">{t('store.title')}</h1>
          <p className="editorial-lede mt-5 max-w-3xl">{t('store.subtitle')}</p>
        </header>

        <section className="py-14" aria-labelledby="original-artworks-title">
          <div className="mb-8 grid gap-3 border-b border-border pb-5 md:grid-cols-[1fr_1fr] md:items-end">
            <h2 id="original-artworks-title" className="font-display text-4xl font-semibold tracking-[-.03em] md:text-5xl">{t('store.originals')}</h2>
            <p className="justify-self-start text-sm leading-6 text-text-secondary md:max-w-md md:justify-self-end">{t('store.originalsCopy')}</p>
          </div>
          {artworksLoading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((item) => <div key={item} className="skeleton-line h-[430px]" />)}</div>
          ) : artworks.length > 0 ? (
            <div className="grid gap-x-5 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">{artworkCards}</div>
          ) : (
            <div className="quiet-surface px-6 py-16 text-center text-text-secondary">{t('gallery.empty')}</div>
          )}
        </section>

        <section className="border-t border-text-primary py-14" aria-labelledby="printed-editions-title">
          <div className="mb-8 grid gap-3 border-b border-border pb-5 md:grid-cols-[1fr_1fr] md:items-end">
            <div>
              <p className="editorial-eyebrow mb-2">Printify</p>
              <h2 id="printed-editions-title" className="font-display text-4xl font-semibold tracking-[-.03em] md:text-5xl">{t('store.printify')}</h2>
            </div>
            <p className="justify-self-start text-sm leading-6 text-text-secondary md:max-w-md md:justify-self-end">{t('store.printifyCopy')}</p>
          </div>

          {printifyLoading ? (
            <div>
              <p className="mb-5 text-sm text-text-secondary">{t('store.loadingPrints')}</p>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((item) => <div key={item} className="skeleton-line h-[390px]" />)}</div>
            </div>
          ) : printifyError || products.length === 0 ? (
            <div className="quiet-surface px-6 py-14 text-center">
              <p className="text-text-secondary">{t('store.noPrints')}</p>
              {user?.role === 'admin' && <Link to="/admin/printify" className="btn-outline mt-6">Configure Printify</Link>}
            </div>
          ) : (
            <div className="grid gap-x-5 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
              {products.map((product) => (
                <PrintifyCard key={product.id} product={product} shopId={defaultShopId} user={user} onLogin={() => setShowLoginModal(true)} t={t} />
              ))}
            </div>
          )}
        </section>
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onSwitchToRegister={() => setShowLoginModal(false)} returnPath="/store" />
    </div>
  );
};

interface PrintifyCardProps {
  product: PrintifyProduct;
  shopId: string;
  user: any;
  onLogin: () => void;
  t: (key: string, options?: any) => string;
}

const PrintifyCard: React.FC<PrintifyCardProps> = ({ product, shopId, user, onLogin, t }) => {
  const image = product.images?.find((item) => item.is_default) || product.images?.[0];
  const variants = product.variants || [];
  const lowestPrice = variants.length ? Math.min(...variants.map((variant) => variant.price)) : 0;

  const handleClick = (event: React.MouseEvent) => {
    if (!user) {
      event.preventDefault();
      onLogin();
    }
  };

  return (
    <Link to={user ? `/store/product/${shopId}/${product.id}` : '#'} onClick={handleClick} className="group block min-w-0">
      <div className="h-72 overflow-hidden bg-surface">
        {image?.src && <img src={image.src} alt={product.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.025]" loading="lazy" />}
      </div>
      <div className="border-t border-border py-4">
        <span className="editorial-eyebrow block text-[10px]">Printify</span>
        <h3 className="mt-1 min-h-[3rem] font-display text-xl font-semibold leading-tight">{product.title}</h3>
        <div className="mt-3 flex items-center justify-between gap-3 text-sm">
          <span className="font-semibold text-primary">{t('store.from')} ${(lowestPrice / 100).toFixed(2)}</span>
          <span className="text-text-muted">{t('store.options', { count: variants.length })}</span>
        </div>
      </div>
    </Link>
  );
};

export default PrintifyStore;
